import React, { ComponentProps, useCallback, useEffect, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { useSnackbar } from 'notistack';
import { TourDetailsFormResources, useTourDetailsFormQuery } from '~/queries/tours/useTourDetailsFormQueries';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Input,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import { formatDateLong } from '~/services/TimeService';
import { getItinerariesByDepartureForTourOption, updateTourItinerariesByDeparture } from '~/services/ToursService';

import ExtendedRJSForm from '../RJSF/ExtendedRJSForm';

import {
  ItineraryByDepartureFormData,
  TourOptionItinerary,
  TourOptionItineraryFormData,
  extractItineraryFormData,
  packageItineraryFormData,
} from './tourItineraryUtils';

function renderValueForButton(label: string) {
  return function renderValue(value) {
    if (!value) {
      return <div color="#7E8188BB">${label}</div>;
    }
    return value.join(', ');
  };
}

interface Props {
  tourId: string;
}

function compareDepartures(a, b) {
  if (a.tourOptionKey.toLowerCase().startsWith('base')) {
    return -1;
  }
  if (b.tourOptionKey.toLowerCase().startsWith('base')) {
    return 1;
  }
  if (a.tourOptionKey.toLowerCase().startsWith('Extended')) {
    return -1;
  }
  if (b.tourOptionKey.toLowerCase().startsWith('Extended')) {
    return 1;
  }
  return a.tourOptionKey.localeCompare(b.tourOptionKey);
}

function getFilteredDeparturesForTourOption(
  tourOptionKey: string,
  tourOptionsItineraries: TourOptionItineraryFormData['derived']['formData']['tourOptionItinerary'],
  departures: Record<string, Array<DepartureForSelection>>,
): Array<string> {
  const tourOptionItinerary = tourOptionsItineraries.find((t) => t.tourOptionKey === tourOptionKey);
  if (!tourOptionItinerary) {
    return;
  }
  const departuresForTourOption = Object.values(departures)
    .flatMap((departuresForDate) =>
      departuresForDate.filter((departure) => departure.tourOptionId === tourOptionItinerary.id),
    )
    .map((departure) => departure.departureDate);
  return departuresForTourOption;
}

function addOrRemoveElementFromUniqueArray(array: Array<string>, value: string) {
  const values = new Set(array);
  if (values.has(value)) {
    values.delete(value);
  } else {
    values.add(value);
  }
  return Array.from(values);
}

type DepartureForSelection = {
  departureId: string;
  tourOptionId: string;
  departureDate: string;
  tourOptionKey: string;
  isActive: boolean;
};

export default function TourItinerariesDetailsForm({ tourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const { fetchRequestInstance, fetch, patchRequestInstance, patch } = useTourDetailsFormQuery(
    TourDetailsFormResources.TOUR_OPTIONS,
  );
  const { fetchRequestInstance: fetchDeparturesRequestInstance, fetch: fetchDepartures } = useTourDetailsFormQuery(
    TourDetailsFormResources.DEPARTURES,
  );
  const [itineraryFormComponents, setItineraryFormComponents] = useState<TourOptionItineraryFormData>(null);
  const [departures, setDepartures] = useState<Record<string, Array<DepartureForSelection>>>({});
  const [selectedTourOptionItineraries, setSelectedTourOptionItineraries] = useState<Array<string>>([]);
  const [selectedDepartures, setSelectedDepartures] = useState<Array<string>>([]);

  useEffect(() => {
    fetch(tourId);
    fetchDepartures(tourId);
  }, [tourId, fetch, fetchDepartures]);

  const handleSubmission = useCallback<ComponentProps<typeof ExtendedRJSForm>['onSubmit']>(
    async ({ formData }) => {
      const itinerariesByDeparture: ItineraryByDepartureFormData = formData as ItineraryByDepartureFormData;
      const pastTourOptionItineraries = itineraryFormComponents.derived.formData.tourOptionItinerary;
      const updatedTourOptionItineraries = itinerariesByDeparture.tourOptionItinerary;
      for (const updatedTourOptionItinerary of updatedTourOptionItineraries) {
        const pastTourOptionItinerary = pastTourOptionItineraries.find(
          (tourOptionItinerary) => tourOptionItinerary.tourOptionKey === updatedTourOptionItinerary.tourOptionKey,
        );
        // Reset the departure id and date after cloning.
        if (updatedTourOptionItinerary.hasBeenCloned) {
          if (pastTourOptionItinerary.departureId) {
            updatedTourOptionItinerary.departureId = pastTourOptionItinerary.departureId;
            updatedTourOptionItinerary.departureDate = pastTourOptionItinerary.departureDate;
          } else {
            delete updatedTourOptionItinerary.departureId;
            delete updatedTourOptionItinerary.departureDate;
          }
          delete updatedTourOptionItinerary.hasBeenCloned;
        }
        if (updatedTourOptionItinerary.departureId) {
          await updateTourItinerariesByDeparture(
            tourId,
            updatedTourOptionItinerary.id,
            updatedTourOptionItinerary.departureId,
            {
              itineraries: updatedTourOptionItinerary.day,
            },
          );
        }
      }
      // Find and purge any deleted tour option itineraries.
      // Note, that root tour options that have been deleted will be reinstated.
      let deletedRootTourOptionFound = false;
      const tourOptionItinerariesToPresent: Array<TourOptionItinerary> = [];
      for (const pastTourOptionItinerary of pastTourOptionItineraries) {
        const updatedTourOptionItinerary = updatedTourOptionItineraries.find(
          (tourOptionItinerary) => tourOptionItinerary.tourOptionKey === pastTourOptionItinerary.tourOptionKey,
        );
        if (!updatedTourOptionItinerary) {
          if ('departureId' in pastTourOptionItinerary) {
            await updateTourItinerariesByDeparture(
              tourId,
              pastTourOptionItinerary.id,
              pastTourOptionItinerary.departureId,
              {
                itineraries: [],
              },
            );
          } else {
            enqueueSnackbar(
              `Tour option itinerary "${pastTourOptionItinerary.tourOptionKey}" cannot be deleted and has been restored`,
              { variant: 'warning' },
            );
            tourOptionItinerariesToPresent.push(cloneDeep(pastTourOptionItinerary) as TourOptionItinerary);
            deletedRootTourOptionFound = true;
          }
        } else {
          tourOptionItinerariesToPresent.push(updatedTourOptionItinerary);
        }
      }
      let patchData;
      if (!deletedRootTourOptionFound) {
        patchData = packageItineraryFormData(itineraryFormComponents.base.formData, itinerariesByDeparture);
      } else {
        // Reinstate the deleted root tour option in both the form in the state and form to be submitted.
        itineraryFormComponents.derived.formData.tourOptionItinerary = tourOptionItinerariesToPresent;
        setItineraryFormComponents(cloneDeep(itineraryFormComponents));
        const itinerariesByDepartureToPresent = { tourOptionItinerary: tourOptionItinerariesToPresent };
        patchData = packageItineraryFormData(itineraryFormComponents.base.formData, itinerariesByDepartureToPresent);
      }
      patch(tourId, patchData);
    },
    [tourId, itineraryFormComponents, patch],
  );

  const processDepartureForForm = useCallback(async function (departures) {
    const departuresByDate = departures.reduce((acc, departure) => {
      const { departureDate } = departure;
      if (!Array.isArray(acc[departureDate])) {
        acc[departureDate] = [];
      }
      acc[departureDate].push({ ...departure, isActive: false });
      return acc;
    }, {});
    return departuresByDate;
  }, []);

  const processItineraryFormData = useCallback(
    async function (formData) {
      const itineraryFormData = extractItineraryFormData(formData);
      const tourOptionsWithDepartures = [];
      for (const tourOption of itineraryFormData.derived.formData.tourOptionItinerary) {
        if (tourOption.departureId) {
          continue;
        }
        // Capture all of the tour options for the tour that will be displayed in the form
        tourOptionsWithDepartures.push(tourOption);
        const response = await getItinerariesByDepartureForTourOption(tourId, tourOption.id);
        // Prepare the itineraries by departure for the form
        const itinerariesByDeparture = response.result.map((to) =>
          omit(
            {
              ...to,
              tourOptionKey: `${to.tourOptionKey} - Departing ${formatDateLong(to.departureDate)}`,
              day: to.itineraries,
            },
            ['itineraries'],
          ),
        );
        tourOptionsWithDepartures.push(
          ...itinerariesByDeparture.sort((a, b) => (a.departureDate > b.departureDate ? 1 : -1)),
        );
      }
      // Replace the tour options with the tour options with the itineraries by departure
      itineraryFormData.derived.formData.tourOptionItinerary = tourOptionsWithDepartures;
      return itineraryFormData;
    },
    [tourId],
  );

  useEffect(() => {
    if (fetchRequestInstance.status === 'succeeded' && fetchDeparturesRequestInstance.status === 'succeeded') {
      // Process the data required for the form
      processItineraryFormData(fetchRequestInstance.result).then((data) => {
        setItineraryFormComponents(data);
        processDepartureForForm(fetchDeparturesRequestInstance.result.departures).then((data) => {
          setDepartures(data);
        });
      });
    }
  }, [fetchRequestInstance, fetchDeparturesRequestInstance]);

  useEffect(() => {
    switch (patchRequestInstance.status) {
      case 'succeeded':
        enqueueSnackbar('Tour itineraries updated successfully.', { variant: 'success' });
        break;
      case 'failed':
        enqueueSnackbar(
          `Submission failed!${patchRequestInstance.status === 'failed' ? patchRequestInstance.error : ''}`,
          { variant: 'error' },
        );
        break;
    }
  }, [patchRequestInstance, enqueueSnackbar]);

  const handleDuplicateAndCreateItineraryByDeparture = useCallback(async () => {
    const itineraryFormComponentsCopy = cloneDeep(itineraryFormComponents);
    selectedTourOptionItineraries.forEach((key) => {
      // Find the tour option itinerary for the selected tour option
      const tourOptionItinerary = itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.find(
        (t) => t.tourOptionKey === key,
      );
      if (!tourOptionItinerary) {
        return;
      }
      // For each departure, create a new itinerary by departure for the tour option
      selectedDepartures.forEach((departure) => {
        const departureForTourOption = departures[departure]?.find((d) => d.tourOptionId === tourOptionItinerary.id);
        if (!departureForTourOption) {
          return;
        }
        const tourOptionCopy = cloneDeep(tourOptionItinerary);
        tourOptionCopy.tourOptionKey = `${tourOptionItinerary.tourOptionKey} - Departing ${formatDateLong(departure)}`;
        tourOptionCopy.departureId = departureForTourOption.departureId;
        tourOptionCopy.departureDate = departure;
        let baseIndex = itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.findIndex(
          (t) => t.id === tourOptionCopy.id,
        );
        if (baseIndex === undefined) {
          return;
        }
        // Place the new itinerary by departure after the selected tour option itinerary. Note, that other
        // itineraries for the same tour option will be accumulated after the selected tour option itinerary.
        // They will be ordered by departure date.
        baseIndex++;
        if (baseIndex >= itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.length) {
          itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.push(tourOptionCopy);
          updateTourItinerariesByDeparture(tourId, tourOptionItinerary.id, departureForTourOption.departureId, {
            itineraries: tourOptionCopy.day,
          });
          return;
        }
        for (; baseIndex < itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.length; baseIndex++) {
          const currentTourOptionItinerary =
            itineraryFormComponentsCopy.derived.formData.tourOptionItinerary[baseIndex];
          if (currentTourOptionItinerary.id !== tourOptionCopy.id) {
            itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.splice(baseIndex, 0, tourOptionCopy);
            updateTourItinerariesByDeparture(
              tourId,
              currentTourOptionItinerary.id,
              departureForTourOption.departureId,
              {
                itineraries: tourOptionCopy.day,
              },
            );
            break;
          }
          if (departure === currentTourOptionItinerary.departureDate) {
            return;
          }
          if (departure < currentTourOptionItinerary.departureDate) {
            itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.splice(baseIndex, 0, tourOptionCopy);
            // Don't submit the form data when there are no days in the itinerary
            updateTourItinerariesByDeparture(
              tourId,
              currentTourOptionItinerary.id,
              departureForTourOption.departureId,
              {
                itineraries: tourOptionCopy.day,
              },
            );
            return;
          }
          if (baseIndex === itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.length - 1) {
            itineraryFormComponentsCopy.derived.formData.tourOptionItinerary.push(tourOptionCopy);
            return;
          }
        }
      });
    });
    setItineraryFormComponents(itineraryFormComponentsCopy);
    setSelectedTourOptionItineraries([]);
    setSelectedDepartures([]);
  }, [itineraryFormComponents, selectedTourOptionItineraries, selectedDepartures, tourId, departures]);

  // Don't render until data is available
  if (
    fetchRequestInstance.status === 'pending' ||
    fetchRequestInstance.status === 'uninitiated' ||
    fetchDeparturesRequestInstance.status === 'pending' ||
    fetchDeparturesRequestInstance.status === 'uninitiated'
  ) {
    return <CircularProgress />;
  }

  if (fetchRequestInstance.status === 'failed' || fetchDeparturesRequestInstance.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" variant="outlined" onClick={() => fetch(tourId)}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the form.</AlertTitle>
        {fetchRequestInstance.status === 'failed'
          ? fetchRequestInstance.error
          : fetchDeparturesRequestInstance.status === 'failed'
          ? fetchDeparturesRequestInstance.error
          : ''}
      </Alert>
    );
  }

  const { result } = fetchRequestInstance;
  const itineraryFormComponentsToRender = itineraryFormComponents || extractItineraryFormData(result);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <Box sx={{ width: 300 }}>
          <InputLabel htmlFor="multi-select-tour-options-widget">Tour Option Itineraries</InputLabel>
          <Select
            labelId="multi-select-tour-options-widget-label"
            label="Tour Options"
            multiple
            fullWidth
            value={selectedTourOptionItineraries}
            onChange={(event, child) => {
              event.preventDefault();

              const { props } = child as { props: { value: string } };

              if (!selectedTourOptionItineraries.includes(props.value)) {
                const departuresForTourOption = getFilteredDeparturesForTourOption(
                  props.value,
                  itineraryFormComponentsToRender.derived.formData.tourOptionItinerary,
                  departures,
                );
                setSelectedTourOptionItineraries([props.value]);
                setSelectedDepartures([]);
                const departureClone = cloneDeep(departures);
                departuresForTourOption.forEach((departure) => {
                  departureClone[departure].forEach((d) => {
                    d.isActive = true;
                  });
                });
                setDepartures(departureClone);
              } else {
                setSelectedTourOptionItineraries([]);
                setSelectedDepartures([]);
                const departureClone = cloneDeep(departures);
                Object.values(departureClone).forEach((departure) => {
                  departure.forEach((d) => {
                    d.isActive = false;
                  });
                });
                setDepartures(departureClone);
              }
            }}
            renderValue={renderValueForButton('Select Tour Option Itinerary')}
            input={<Input value={selectedTourOptionItineraries.join(', ')} />}
            MenuProps={{
              autoFocus: false,
              PaperProps: { sx: { maxHeight: 300 } },
            }}
          >
            {itineraryFormComponentsToRender.derived.formData.tourOptionItinerary
              .filter((t) => t.departureId === undefined)
              .sort(compareDepartures)
              .map((value, index) => {
                return (
                  <MenuItem key={`"${index}"`} value={value.tourOptionKey}>
                    <Checkbox checked={selectedTourOptionItineraries.includes(value.tourOptionKey)} />
                    {value.tourOptionKey}
                  </MenuItem>
                );
              })}
          </Select>
        </Box>
        <Box sx={{ width: 300 }}>
          <InputLabel htmlFor="multi-select-departures-widget">Departures</InputLabel>
          <Select
            id="multi-select-departures-widget"
            label="Departures"
            multiple
            fullWidth
            value={Array.from(selectedDepartures)}
            onChange={(event, child) => {
              event.preventDefault();
              const { props } = child as { props: { value: string } };
              setSelectedDepartures(addOrRemoveElementFromUniqueArray(selectedDepartures, props.value));
            }}
            renderValue={renderValueForButton('Select Departure')}
            input={<Input value={selectedDepartures.join(', ')} />}
            MenuProps={{
              autoFocus: false,
              PaperProps: { sx: { maxHeight: 300 } },
            }}
          >
            {selectedTourOptionItineraries.length > 0 ? (
              Array.from(new Set(Object.keys(departures).filter((d) => departures[d].some((d) => d.isActive))))
                .sort()
                .map((departureDate, index) => {
                  return (
                    <MenuItem key={index} value={departureDate}>
                      <Checkbox checked={selectedDepartures.includes(departureDate)} />
                      {departureDate}
                    </MenuItem>
                  );
                })
            ) : (
              <MenuItem value="">Select a tour option itinerary</MenuItem>
            )}
          </Select>
        </Box>
        <Box>
          <Button onClick={handleDuplicateAndCreateItineraryByDeparture}>
            Duplicate & Create Itinerary By Departure/s
          </Button>
        </Box>
      </Box>
      <ExtendedRJSForm
        id="tour-itineraries-details-form"
        schema={itineraryFormComponentsToRender.derived.schema}
        uiSchema={itineraryFormComponentsToRender.derived.uiSchema}
        formData={itineraryFormComponentsToRender.derived.formData}
        onSubmit={handleSubmission}
        busy={patchRequestInstance.status === 'pending'}
      />
      {patchRequestInstance.status === 'failed' && (
        <Alert severity="error">{patchRequestInstance.status === 'failed' ? patchRequestInstance.error : ''}</Alert>
      )}
    </>
  );
}
